import { useEffect, useState } from "react";
import { Button, Card, Col, Row } from "react-bootstrap";
import SimpleTable from "components/global/SimpleTable";
import SimpleTableFilterDropdown from "components/global/SimpleTableFilterDropdown";
import { useHistory } from "react-router-dom";
import {
  fetchAndSetPrograms,
  handleDownloadFundingProgramsPerformanceData,
  handleEdoPerformanceFilterChange,
  handlePerformanceClearFilters
} from "./EdoPerformance.helpers";
import edoFundingSignal from "./EdoPerformance.signals";
import BreadCrumbWrapper from "components/global/BreadCrumbs/BreadCrumbs";
import { checkEdoPermissionsAcrossPrograms } from '../../../../libs/edoPermissions';
import userAccountSignal from '../../../../signals/UserAccount.signal';
import api from '../../../../api/api';
import loaderSignal from '../../../../signals/Loader.signal';
import currencyOptions from 'components/global/Constant/currencyOptions';

const HEADERS = [
  {
    key: 'id',
    title: 'ID',
    sortable: true,
  },
  {
    key: 'name',
    title: 'Name',
  },
  {
    key: 'platform',
    title: 'Platform',
  },
  {
    key: 'programAllocated',
    title: 'Program Funds',
    currency: true,
  },
  {
    key: 'obligated',
    title: 'Obligated',
    currency: true,
  },
  {
    key: 'expended',
    title: 'Expended',
    currency: true,
  },
  {
    key: 'remaining',
    title: 'Remaining',
    currency: true,
  },
  {
    key: 'recycled',
    title: 'Recycled',
    currency: true,
  },
];

const EdoPerformance = () => {
  const { sort, dropdownFilters, platforms, fundingPrograms } =
    edoFundingSignal.value;
  const history = useHistory();
  const [canView, setCanView] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  // Check if user has canView permission on any program
  useEffect(() => {
    const checkViewPermission = async () => {
      try {
        setIsChecking(true);
        loaderSignal.update({ isContentLoading: true });
        const userData = userAccountSignal.value.userData;
        
        // Wait for userData to be loaded
        if (!userData) {
          setIsChecking(false);
          loaderSignal.reset();
          return;
        }

        // Fetch funding programs for the EDO
        const fundingPrograms = await api.get({
          path: '/fundingPrograms',
          options: {
            where: {
              edoId: userData?.account.edoId,
            },
          },
        });

        const permissions = await checkEdoPermissionsAcrossPrograms(
          fundingPrograms,
          ['canView']
        );
        setCanView(permissions.canView);
        if (!permissions.canView) {
          loaderSignal.reset();
        }
      } catch (error) {
        console.error('Error checking view permission:', error);
        setCanView(false);
        loaderSignal.reset();
      } finally {
        setIsChecking(false);
      }
    };

    checkViewPermission();
  }, [userAccountSignal.value.userData]);

  useEffect(() => {
    if (canView) {
      fetchAndSetPrograms();
    }
  }, [history.location.search, canView]);

  if (isChecking) {
    return null;
  }

  if (!canView) {
    return (
      <BreadCrumbWrapper>
        <Card className="rounded-0">
          <Card.Body>
            <h3 className="text-center">Access Denied</h3>
            <p className="text-center">
              You do not have permission to view performance data.
            </p>
          </Card.Body>
        </Card>
      </BreadCrumbWrapper>
    );
  }

  const activePlatformId = dropdownFilters.platformName
    ? Number(dropdownFilters.platformName)
    : null;
  const activePlatform = activePlatformId
    ? platforms.find((p) => p.id === activePlatformId)
    : undefined;

  const sidebarPrograms = activePlatformId
    ? fundingPrograms.filter((fp) => fp.platform?.id === activePlatformId)
    : fundingPrograms;

  const sumFpStat = (key) =>
    sidebarPrograms.reduce((acc, fp) => acc + (fp.stats?.[key] || 0), 0);

  const platformsForSidebar = activePlatform ? [activePlatform] : platforms;
  const burnedTotal = platformsForSidebar.reduce((acc, p) => acc + (p.stats?.burned || 0), 0);
  const fundedTotal = platformsForSidebar.reduce((acc, p) => acc + (p.stats?.funded || 0), 0);

  return (
    <BreadCrumbWrapper>
      <Row>
        <Col xs={9}>
          <Card className="rounded-0">
            <Card.Body>
              <h3 className="text-center mb-32">Program Performance</h3>
              <hr />
              <div className="d-flex flex-row justify-content-between mb-16">
                <SimpleTableFilterDropdown
                  filtersState={dropdownFilters}
                  onChange={({ name, value }) =>
                    handleEdoPerformanceFilterChange(name, value)
                  }
                  onApply={() => {
                    fetchAndSetPrograms();
                  }}
                  filters={[
                    {
                      type: 'integer',
                      label: 'ID',
                      key: 'id',
                    },
                    {
                      type: 'select',
                      label: 'Platform',
                      key: 'platformName',
                      options: platforms.map((p) => ({
                        label: p.name,
                        value: p.id,
                      })),
                    },
                  ]}
                  onClearFilterInputs={handlePerformanceClearFilters}
                />
                <Button
                  onClick={handleDownloadFundingProgramsPerformanceData}
                  className="rounded"
                >
                  Download
                </Button>
              </div>
              <SimpleTable
                headers={HEADERS}
                sort={sort}
                onClick={(index) => {
                  let programType = 'credit-program';
                  if (fundingPrograms[index].referenceProgram.programType.programTypeName === 'Equity') {
                    programType = 'equity-program';
                  }

                  const path = `/edo/performance/${programType}/${fundingPrograms[index].id}`;
                  history.push(path);
                }}
                onSort={() => {
                  fetchAndSetPrograms();
                }}
                rows={fundingPrograms.map((fundingProgram) => ({
                  id: fundingProgram.id,
                  name: `${fundingProgram.name} - ${fundingProgram.referenceProgram.programName}`,
                  platformType: fundingProgram.platform.platformType,
                  programAllocated:
                    fundingProgram.ceiling -
                    fundingProgram.fundsAlreadyAllocated,
                  obligated: fundingProgram.stats.obligated,
                  expended: fundingProgram.stats.expended,
                  remaining: fundingProgram.stats.remaining,
                  recycled: fundingProgram.stats.recycled,
                }))}
              />
            </Card.Body>
          </Card>
        </Col>
        <Col>
          <Card className="rounded-0">
            <Card.Body>
              <h4>
                {activePlatform ? activePlatform.platformType : 'All Platforms'}
              </h4>
              <hr />
              {/*<ProgressBar*/}
              {/*  total={*/}
              {/*    activePlatform*/}
              {/*      ? activePlatform.stats.burned*/}
              {/*      : reducedStats.burned*/}
              {/*  }*/}
              {/*  segments={[*/}
              {/*    {*/}
              {/*      amount: activePlatform*/}
              {/*        ? activePlatform.stats.funded*/}
              {/*        : reducedStats.funded,*/}
              {/*      color: COLORS.allocated,*/}
              {/*      title: 'Funded',*/}
              {/*    },*/}
              {/*    {*/}
              {/*      amount: activePlatform*/}
              {/*        ? activePlatform.stats.obligated*/}
              {/*        : reducedStats.obligated,*/}
              {/*      color: COLORS.obligated,*/}
              {/*      title: 'Obligated',*/}
              {/*    },*/}
              {/*    {*/}
              {/*      amount: activePlatform*/}
              {/*        ? activePlatform.stats.expended*/}
              {/*        : reducedStats.expended,*/}
              {/*      color: COLORS.expended,*/}
              {/*      title: 'Expended',*/}
              {/*    },*/}
              {/*  ]}*/}
              {/*/>*/}
              <br />
              <h5 className="font-600">Money Received</h5>
              <p>
                {burnedTotal?.toLocaleString('en-US', currencyOptions.currencyUS)}
              </p>
              <h5 className="font-600">Unfunded</h5>
              <p>
                {(burnedTotal - fundedTotal)?.toLocaleString('en-US', currencyOptions.currencyUS)}
              </p>
              <h5 className="font-600">Funded</h5>
              <p>
                {fundedTotal?.toLocaleString('en-US', currencyOptions.currencyUS)}
              </p>
              <h5 className="font-600">Obligated</h5>
              <p>
                {sumFpStat('obligated')?.toLocaleString('en-US', currencyOptions.currencyUS)}
              </p>
              <h5 className="font-600">Expended</h5>
              <p>
                {sumFpStat('expended')?.toLocaleString('en-US', currencyOptions.currencyUS)}
              </p>
              <h5 className="font-600">Remaining</h5>
              <p>
                {sumFpStat('remaining')?.toLocaleString('en-US', currencyOptions.currencyUS)}
              </p>
              <h5 className="font-600">Recycled</h5>
              <p>
                {sumFpStat('recycled')?.toLocaleString('en-US', currencyOptions.currencyUS)}
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </BreadCrumbWrapper>
  );
};

export default EdoPerformance;
